/**
 * OverviewRenderer — Renders the Overview tab for the Enterprise Architecture page.
 *
 * Displays:
 *   1. Stats cards row (headcount, divisions, departments, teams, etc.)
 *   2. Division breakdown list
 *   3. Zachman Framework matrix populated from real data
 *   4. Applications by domain horizontal bar chart
 *
 * All content is derived from the supplied data; nothing is hardcoded.
 * Uses vanilla DOM manipulation with no external dependencies.
 */

// ============================================================================
// Data interfaces
// ============================================================================

export interface EAStatistics {
    total_organizations: number;
    total_applications: number;
    total_technical_services: number;
    total_business_services: number;
    total_data_objects: number;
    total_hr_roles: number;
    total_relationships: number;
    organizations_by_division: Array<[string, number]>;
    applications_by_domain: Array<[string, number]>;
    applications_by_criticality: Array<[string, number]>;
    total_headcount: number;
}

export interface OverviewData {
    stats: EAStatistics;
    organizations?: Array<Record<string, unknown>>;
    applications?: Array<Record<string, unknown>>;
    technicalServices?: Array<Record<string, unknown>>;
    businessServices?: Array<Record<string, unknown>>;
    dataObjects?: Array<Record<string, unknown>>;
    hrRoles?: Array<Record<string, unknown>>;
}

// ============================================================================
// Zachman cell descriptor
// ============================================================================

interface ZachmanCell {
    title: string;
    count: number;
    details: string[];
}

// ============================================================================
// Constants
// ============================================================================

const ZACHMAN_COLUMNS = ['What (Data)', 'How (Function)', 'Where (Network)', 'Who (People)', 'When (Time)', 'Why (Motivation)'];

const ZACHMAN_ROWS = [
    'Scope / Planner',
    'Business Model / Owner',
    'System Model / Designer',
    'Technology / Builder',
    'Component / Subcontractor',
    'Operations / Worker',
];

const ZACHMAN_ROW_TINTS = [
    '#f0fdf4',
    '#eff6ff',
    '#fefce8',
    '#fff7ed',
    '#fdf2f8',
    '#f5f3ff',
];

// ============================================================================
// Helpers
// ============================================================================

function formatNumber(n: number): string {
    return n.toLocaleString('en-US');
}

function escapeHtml(s: string): string {
    const div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
}

/**
 * Extract the first N distinct string values for a given key from an array of records.
 */
function extractNames(items: Array<Record<string, unknown>> | undefined, key: string, limit: number): string[] {
    if (!items || items.length === 0) return [];
    const names: string[] = [];
    for (const item of items) {
        const val = item[key];
        const name = typeof val === 'string' ? val.trim() : '';
        if (name && !names.includes(name)) {
            names.push(name);
        }
        if (names.length >= limit) break;
    }
    return names;
}

/**
 * Count distinct values for a given key in an array of records.
 */
function countDistinct(items: Array<Record<string, unknown>> | undefined, key: string): number {
    if (!items || items.length === 0) return 0;
    const seen = new Set<string>();
    for (const item of items) {
        const val = item[key];
        if (typeof val === 'string' && val.trim()) {
            seen.add(val.trim().toLowerCase());
        }
    }
    return seen.size;
}

// ============================================================================
// OverviewRenderer
// ============================================================================

export class OverviewRenderer {
    private container: HTMLElement;

    constructor(container: HTMLElement) {
        this.container = container;
    }

    // -----------------------------------------------------------------------
    // Public API
    // -----------------------------------------------------------------------

    public render(data: OverviewData): void {
        this.clearContainer();

        if (this.isDataEmpty(data)) {
            this.showEmptyState('No EA data imported yet. Use the Import tab to upload an EA spreadsheet.');
            return;
        }

        const wrapper = document.createElement('div');
        wrapper.style.cssText = 'display:flex;flex-direction:column;gap:24px;padding:16px 0;';

        wrapper.appendChild(this.renderStatsCards(data));

        const divisionSection = this.renderDivisionBreakdown(data.stats.organizations_by_division);
        if (divisionSection) {
            wrapper.appendChild(divisionSection);
        }

        wrapper.appendChild(this.renderZachmanMatrix(data));

        const domainSection = this.renderApplicationsByDomain(data.stats.applications_by_domain);
        if (domainSection) {
            wrapper.appendChild(domainSection);
        }

        this.container.appendChild(wrapper);
    }

    public destroy(): void {
        this.clearContainer();
    }

    // -----------------------------------------------------------------------
    // Section 1: Stats Cards
    // -----------------------------------------------------------------------

    private renderStatsCards(data: OverviewData): HTMLElement {
        const { stats } = data;

        const divisionCount = stats.organizations_by_division.length;
        const departmentCount = countDistinct(data.organizations, 'department') || countDistinct(data.organizations, 'Department') || (data.organizations ? data.organizations.length : 0);
        const teamCount = data.organizations ? data.organizations.length : 0;

        const businessCapabilities = this.countBusinessCapabilities(data);

        const cards: Array<{ value: number; label: string }> = [
            { value: stats.total_headcount, label: 'Total Headcount' },
            { value: divisionCount, label: 'Divisions' },
            { value: departmentCount, label: 'Departments' },
            { value: teamCount, label: 'Teams' },
            { value: businessCapabilities, label: 'Business Capabilities' },
            { value: stats.total_applications, label: 'Applications' },
            { value: stats.total_technical_services, label: 'Technical Services' },
            { value: stats.total_data_objects, label: 'Data Objects' },
        ];

        const row = document.createElement('div');
        row.className = 'ea-stats';
        row.style.cssText = 'display:flex;flex-wrap:wrap;gap:12px;';

        for (const card of cards) {
            const cardEl = document.createElement('div');
            cardEl.className = 'ea-stat-card';
            cardEl.style.cssText = 'background:#fff;border:1px solid #dee2e6;border-radius:8px;padding:12px 20px;box-shadow:0 1px 3px rgba(0,0,0,.12);min-width:120px;text-align:center;flex:1 1 auto;';

            const numEl = document.createElement('div');
            numEl.style.cssText = 'font-size:28px;font-weight:700;color:#0d6efd;';
            numEl.textContent = formatNumber(card.value);

            const labelEl = document.createElement('div');
            labelEl.style.cssText = 'font-size:12px;color:#6c757d;margin-top:4px;';
            labelEl.textContent = card.label;

            cardEl.appendChild(numEl);
            cardEl.appendChild(labelEl);
            row.appendChild(cardEl);
        }

        return row;
    }

    /**
     * Count business capabilities from organizations metadata or business services.
     */
    private countBusinessCapabilities(data: OverviewData): number {
        // Try extracting capabilities from organizations metadata
        if (data.organizations && data.organizations.length > 0) {
            const capSet = new Set<string>();
            for (const org of data.organizations) {
                const caps = org['capabilities'] ?? org['business_capabilities'] ?? org['Capabilities'];
                if (typeof caps === 'string' && caps.trim()) {
                    for (const c of caps.split(/[,;]/)) {
                        const trimmed = c.trim();
                        if (trimmed) capSet.add(trimmed.toLowerCase());
                    }
                } else if (Array.isArray(caps)) {
                    for (const c of caps) {
                        if (typeof c === 'string' && c.trim()) capSet.add(c.trim().toLowerCase());
                    }
                }
            }
            if (capSet.size > 0) return capSet.size;
        }

        // Fall back to business services count
        if (data.stats.total_business_services > 0) {
            return data.stats.total_business_services;
        }

        return 0;
    }

    // -----------------------------------------------------------------------
    // Section 2: Division Breakdown
    // -----------------------------------------------------------------------

    private renderDivisionBreakdown(divisions: Array<[string, number]>): HTMLElement | null {
        if (!divisions || divisions.length === 0) return null;

        const section = document.createElement('div');
        section.style.cssText = 'background:#fff;border:1px solid #dee2e6;border-radius:8px;padding:16px 20px;box-shadow:0 1px 3px rgba(0,0,0,.12);';

        const heading = document.createElement('h3');
        heading.style.cssText = 'margin:0 0 12px 0;font-size:16px;font-weight:600;color:#1e293b;';
        heading.textContent = 'Division Breakdown';
        section.appendChild(heading);

        const list = document.createElement('div');
        for (const [name, count] of divisions) {
            const row = document.createElement('div');
            row.style.cssText = 'display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px solid #f1f5f9;';

            const nameEl = document.createElement('span');
            nameEl.style.cssText = 'color:#334155;font-size:13px;';
            nameEl.textContent = name;

            const countEl = document.createElement('span');
            countEl.style.cssText = 'color:#0d6efd;font-weight:600;font-size:13px;';
            countEl.textContent = formatNumber(count);

            row.appendChild(nameEl);
            row.appendChild(countEl);
            list.appendChild(row);
        }
        section.appendChild(list);

        return section;
    }

    // -----------------------------------------------------------------------
    // Section 3: Zachman Framework Matrix
    // -----------------------------------------------------------------------

    private renderZachmanMatrix(data: OverviewData): HTMLElement {
        const section = document.createElement('div');
        section.style.cssText = 'background:#fff;border:1px solid #dee2e6;border-radius:8px;padding:16px 20px;box-shadow:0 1px 3px rgba(0,0,0,.12);overflow-x:auto;';

        const heading = document.createElement('h3');
        heading.style.cssText = 'margin:0 0 12px 0;font-size:16px;font-weight:600;color:#1e293b;';
        heading.textContent = 'Zachman Framework';
        section.appendChild(heading);

        const table = document.createElement('table');
        table.style.cssText = 'border-collapse:collapse;width:100%;font-size:12px;';

        // Header row
        const thead = document.createElement('thead');
        const headerRow = document.createElement('tr');

        // Empty corner cell
        const cornerTh = document.createElement('th');
        cornerTh.style.cssText = 'background:#1e293b;color:#fff;font-weight:700;text-align:center;padding:8px 10px;border:2px solid #333;width:140px;';
        cornerTh.textContent = 'Perspective';
        headerRow.appendChild(cornerTh);

        for (const col of ZACHMAN_COLUMNS) {
            const th = document.createElement('th');
            th.style.cssText = 'background:#1e293b;color:#fff;font-weight:700;text-align:center;padding:8px 10px;border:2px solid #333;';
            th.textContent = col;
            headerRow.appendChild(th);
        }
        thead.appendChild(headerRow);
        table.appendChild(thead);

        // Build cell data grid
        const grid = this.buildZachmanGrid(data);

        // Body rows
        const tbody = document.createElement('tbody');
        for (let rowIdx = 0; rowIdx < ZACHMAN_ROWS.length; rowIdx++) {
            const tr = document.createElement('tr');

            // Row header
            const rowHeader = document.createElement('td');
            rowHeader.style.cssText = 'background:#334155;color:#fff;text-align:left;width:140px;font-weight:700;padding:8px 10px;border:2px solid #333;';
            rowHeader.textContent = ZACHMAN_ROWS[rowIdx];
            tr.appendChild(rowHeader);

            // Data cells
            const tint = ZACHMAN_ROW_TINTS[rowIdx];
            for (let colIdx = 0; colIdx < ZACHMAN_COLUMNS.length; colIdx++) {
                const cell = grid[rowIdx][colIdx];
                const td = document.createElement('td');
                td.style.cssText = `background:${tint};border:2px solid #333;padding:8px 10px;vertical-align:top;`;

                if (cell.count > 0 || cell.details.length > 0) {
                    td.innerHTML = this.renderZachmanCellContent(cell);
                } else {
                    td.innerHTML = '<span style="color:#94a3b8;font-style:italic;">--</span>';
                }

                tr.appendChild(td);
            }

            tbody.appendChild(tr);
        }
        table.appendChild(tbody);
        section.appendChild(table);

        return section;
    }

    /**
     * Build a 6x6 grid of ZachmanCell objects populated from real data.
     *
     * Columns: What(Data), How(Function), Where(Network), Who(People), When(Time), Why(Motivation)
     * Rows:    Scope, Business, System, Technology, Component, Operations
     */
    private buildZachmanGrid(data: OverviewData): ZachmanCell[][] {
        const { stats } = data;

        const orgNames = extractNames(data.organizations, 'name', 5);
        const appNames = extractNames(data.applications, 'name', 5);
        const techNames = extractNames(data.technicalServices, 'name', 5);
        const bizNames = extractNames(data.businessServices, 'name', 5);
        const dataNames = extractNames(data.dataObjects, 'name', 5);
        const roleNames = extractNames(data.hrRoles, 'name', 5);

        const divisionNames = stats.organizations_by_division.map(([n]) => n).slice(0, 5);
        const domainNames = stats.applications_by_domain.map(([n]) => n).slice(0, 5);
        const critNames = stats.applications_by_criticality.map(([n, c]) => `${n} (${c})`).slice(0, 5);

        // Derive some counts
        const divisionCount = stats.organizations_by_division.length;
        const domainCount = stats.applications_by_domain.length;

        // Initialize empty grid
        const grid: ZachmanCell[][] = [];
        for (let r = 0; r < 6; r++) {
            const row: ZachmanCell[] = [];
            for (let c = 0; c < 6; c++) {
                row.push({ title: '', count: 0, details: [] });
            }
            grid.push(row);
        }

        // Row 0: Scope / Planner
        grid[0][0] = { title: 'Business Entities', count: stats.total_data_objects, details: dataNames };
        grid[0][1] = { title: 'Business Processes', count: stats.total_business_services, details: bizNames };
        grid[0][2] = { title: 'Business Locations', count: divisionCount, details: divisionNames };
        grid[0][3] = { title: 'Organizations', count: data.organizations ? data.organizations.length : 0, details: orgNames };
        grid[0][4] = { title: 'Business Events', count: stats.total_relationships, details: stats.total_relationships > 0 ? [`${formatNumber(stats.total_relationships)} relationships tracked`] : [] };
        grid[0][5] = { title: 'Business Goals', count: domainCount, details: domainNames };

        // Row 1: Business Model / Owner
        grid[1][0] = { title: 'Data Model', count: stats.total_data_objects, details: dataNames.slice(0, 3) };
        grid[1][1] = { title: 'Business Services', count: stats.total_business_services, details: bizNames.slice(0, 3) };
        grid[1][2] = { title: 'Divisions', count: divisionCount, details: divisionNames.slice(0, 3) };
        grid[1][3] = { title: 'Roles & Headcount', count: stats.total_hr_roles, details: stats.total_headcount > 0 ? [`${formatNumber(stats.total_headcount)} total headcount`, ...roleNames.slice(0, 2)] : roleNames.slice(0, 3) };
        grid[1][4] = { title: 'Schedules', count: 0, details: [] };
        grid[1][5] = { title: 'Criticality Levels', count: stats.applications_by_criticality.length, details: critNames };

        // Row 2: System Model / Designer
        grid[2][0] = { title: 'Logical Data', count: stats.total_data_objects, details: dataNames.slice(0, 3) };
        grid[2][1] = { title: 'Applications', count: stats.total_applications, details: appNames };
        grid[2][2] = { title: 'App Domains', count: domainCount, details: domainNames.slice(0, 3) };
        grid[2][3] = { title: 'App Users', count: stats.total_hr_roles, details: roleNames.slice(0, 3) };
        grid[2][4] = { title: 'Data Flows', count: stats.total_relationships, details: stats.total_relationships > 0 ? [`${formatNumber(stats.total_relationships)} integration links`] : [] };
        grid[2][5] = { title: 'App Criticality', count: stats.applications_by_criticality.length, details: critNames.slice(0, 3) };

        // Row 3: Technology / Builder
        grid[3][0] = { title: 'Physical Data Stores', count: stats.total_data_objects, details: dataNames.slice(0, 3) };
        grid[3][1] = { title: 'Technical Services', count: stats.total_technical_services, details: techNames };
        grid[3][2] = { title: 'Infrastructure', count: stats.total_technical_services, details: techNames.slice(0, 3) };
        grid[3][3] = { title: 'Technical Roles', count: stats.total_hr_roles, details: roleNames.slice(0, 3) };
        grid[3][4] = { title: 'Integration Timing', count: 0, details: [] };
        grid[3][5] = { title: 'Technology Standards', count: stats.total_technical_services, details: techNames.slice(0, 3) };

        // Row 4: Component / Subcontractor
        grid[4][0] = { title: 'Data Components', count: stats.total_data_objects, details: dataNames.slice(0, 2) };
        grid[4][1] = { title: 'Application Components', count: stats.total_applications, details: appNames.slice(0, 3) };
        grid[4][2] = { title: 'Network Segments', count: divisionCount, details: divisionNames.slice(0, 2) };
        grid[4][3] = { title: 'Teams', count: data.organizations ? data.organizations.length : 0, details: orgNames.slice(0, 3) };
        grid[4][4] = { title: 'Deployment Windows', count: 0, details: [] };
        grid[4][5] = { title: 'Service Levels', count: stats.applications_by_criticality.length, details: critNames.slice(0, 2) };

        // Row 5: Operations / Worker
        grid[5][0] = { title: 'Data Instances', count: stats.total_data_objects, details: [] };
        grid[5][1] = { title: 'Running Services', count: stats.total_applications + stats.total_technical_services, details: [] };
        grid[5][2] = { title: 'Operational Sites', count: divisionCount, details: [] };
        grid[5][3] = { title: 'Active Users', count: stats.total_headcount, details: stats.total_headcount > 0 ? [`${formatNumber(stats.total_headcount)} headcount`] : [] };
        grid[5][4] = { title: 'Operational Cycles', count: 0, details: [] };
        grid[5][5] = { title: 'Operational KPIs', count: stats.total_relationships, details: stats.total_relationships > 0 ? [`${formatNumber(stats.total_relationships)} tracked relationships`] : [] };

        return grid;
    }

    private renderZachmanCellContent(cell: ZachmanCell): string {
        const lines: string[] = [];
        lines.push(`<div style="font-weight:700;color:#1e293b;margin-bottom:2px;">${escapeHtml(cell.title)}</div>`);
        if (cell.count > 0) {
            lines.push(`<div style="color:#0d6efd;font-weight:600;">${formatNumber(cell.count)}</div>`);
        }
        if (cell.details.length > 0) {
            const detailHtml = cell.details.map((d) => `<div style="color:#475569;font-size:11px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:140px;">${escapeHtml(d)}</div>`).join('');
            lines.push(detailHtml);
        }
        return lines.join('');
    }

    // -----------------------------------------------------------------------
    // Section 4: Applications by Domain
    // -----------------------------------------------------------------------

    private renderApplicationsByDomain(domains: Array<[string, number]>): HTMLElement | null {
        if (!domains || domains.length === 0) return null;

        const section = document.createElement('div');
        section.style.cssText = 'background:#fff;border:1px solid #dee2e6;border-radius:8px;padding:16px 20px;box-shadow:0 1px 3px rgba(0,0,0,.12);';

        const heading = document.createElement('h3');
        heading.style.cssText = 'margin:0 0 12px 0;font-size:16px;font-weight:600;color:#1e293b;';
        heading.textContent = 'Applications by Domain';
        section.appendChild(heading);

        // Determine max value for bar scaling
        const maxCount = Math.max(...domains.map(([, c]) => c), 1);

        const list = document.createElement('div');
        list.style.cssText = 'display:flex;flex-direction:column;gap:8px;';

        for (const [name, count] of domains) {
            const row = document.createElement('div');
            row.style.cssText = 'display:flex;align-items:center;gap:10px;';

            const label = document.createElement('span');
            label.style.cssText = 'flex:0 0 160px;font-size:13px;color:#334155;text-align:right;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
            label.textContent = name;

            const barContainer = document.createElement('div');
            barContainer.style.cssText = 'flex:1;background:#f1f5f9;border-radius:4px;height:20px;position:relative;overflow:hidden;';

            const bar = document.createElement('div');
            const widthPercent = Math.max((count / maxCount) * 100, 2);
            bar.style.cssText = `width:${widthPercent}%;height:100%;background:#0d6efd;border-radius:4px;transition:width 0.3s ease;`;

            barContainer.appendChild(bar);

            const countLabel = document.createElement('span');
            countLabel.style.cssText = 'flex:0 0 40px;font-size:13px;font-weight:600;color:#0d6efd;text-align:right;';
            countLabel.textContent = formatNumber(count);

            row.appendChild(label);
            row.appendChild(barContainer);
            row.appendChild(countLabel);
            list.appendChild(row);
        }

        section.appendChild(list);
        return section;
    }

    // -----------------------------------------------------------------------
    // Utility
    // -----------------------------------------------------------------------

    private isDataEmpty(data: OverviewData): boolean {
        const s = data.stats;
        return (
            s.total_organizations === 0 &&
            s.total_applications === 0 &&
            s.total_technical_services === 0 &&
            s.total_business_services === 0 &&
            s.total_data_objects === 0 &&
            s.total_hr_roles === 0 &&
            s.total_relationships === 0 &&
            s.total_headcount === 0
        );
    }

    private clearContainer(): void {
        while (this.container.firstChild) {
            this.container.removeChild(this.container.firstChild);
        }
    }

    private showEmptyState(message: string): void {
        this.clearContainer();
        const wrapper = document.createElement('div');
        wrapper.style.cssText = 'display:flex;align-items:center;justify-content:center;height:100%;min-height:200px;color:#94a3b8;font-size:14px;text-align:center;padding:24px;';
        wrapper.textContent = message;
        this.container.appendChild(wrapper);
    }
}
